import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  Download,
  Handshake,
  Loader2,
  Network,
  PlayCircle,
  Shield,
  Sparkles,
  Target,
  Workflow,
  Zap,
} from 'lucide-react';
import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import MapleOSLogo from '@/components/common/MapleOSLogo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

const formSchema = z.object({
  companyName: z.string().min(2, 'Company name is required.'),
  contactName: z.string().min(2, 'Your name is required.'),
  contactEmail: z.string().email('Invalid email address.'),
  website: z.string().url('Please enter a valid website URL (e.g., https://example.com)').optional().or(z.literal('')),
  message: z.string().min(20, "Please tell us why you'd like to partner with us.").max(2000),
});

const sanctuaryPillars = [
  {
    icon: Sparkles,
    title: 'Intelligence',
    description: 'AI systems, agents, and workflows designed to solve real human problems.',
  },
  {
    icon: Network,
    title: 'Connection',
    description: 'A living ecosystem where data, tools, and models interoperate through orchestration.',
  },
  {
    icon: Workflow,
    title: 'Evolution',
    description: 'Systems refine, expand, and propagate into stronger products over time.',
  },
];

const ecosystemRoles = [
  {
    icon: Handshake,
    title: 'Developer Partner',
    description: 'Build inside the Sanctuary using blueprints, agents, and orchestration to turn AI systems into human-ready products.',
  },
  {
    icon: Zap,
    title: 'Market Partner',
    description: 'Bring MapleOS-powered systems to market, unlock demand, and help intelligence reach the people who need it.',
  },
  {
    icon: Award,
    title: 'Enterprise Partner',
    description: 'Deploy and scale orchestrated AI systems across teams, operations, and customer environments with confidence.',
  },
];

const ecosystemMoments = [
  {
    eyebrow: 'AI Theater',
    title: 'Where systems come to life',
    description:
      'Blueprints define the structure. Theater shows execution. The Orchestrator keeps every system moving in sync.',
  },
  {
    eyebrow: 'Orchestrator',
    title: 'The engine behind the Sanctuary',
    description:
      'Every AI system needs coordination. MapleOS makes agents, tools, and decisions move as one living product.',
  },
  {
    eyebrow: 'Products for Humans',
    title: 'From intelligence to usable outcomes',
    description:
      'Systems built in MapleOS are not just demos. They become products people can use, trust, and deploy.',
  },
];

const partnerBenefits = [
  {
    icon: Shield,
    title: 'Sovereign foundation',
    description: 'Build on a platform shaped around privacy, trust, and real-world deployment needs.',
  },
  {
    icon: PlayCircle,
    title: 'Visual system design',
    description: 'Use Blueprint Canvas and AI Theater to make complex intelligence understandable and operable.',
  },
  {
    icon: Target,
    title: 'Product pathways',
    description: 'Turn reusable workflows, agents, and orchestrated systems into scalable offerings.',
  },
];

export default function PartnershipPage() {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      contactName: '',
      contactEmail: '',
      website: '',
      message: '',
    },
  });

  const downloadDeck = () => {
    const a = document.createElement('a');
    a.href = '/pdfs/partnership-deck.pdf';
    a.download = 'mapleos-Partnership-Deck.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);

    try {
      const { error } = await supabase.functions.invoke('send-partnership-request', {
        body: values,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Request sent',
        description: "Thanks for reaching out. We'll follow up soon.",
      });
      form.reset();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: `Something went wrong: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <LandingShell>
      <SEO
        title="Partnership | mapleos"
        description="Enter the Sanctuary — where AI systems are designed, orchestrated, and deployed into the real world with mapleos."
        url="/partnership"
      />

      <div className="bg-background text-foreground">
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(138,92,246,0.18),transparent_38%),linear-gradient(180deg,rgba(8,12,20,0.96),rgba(8,12,20,1))]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8 lg:py-28">
            <div>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl"
              >
                <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <MapleOSLogo textOnly className="h-[0.95em] w-auto text-white" />
                  <span>helps humans solve</span>
                  <span className="text-primary">real problems.</span>
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.06 }}
                className="mt-6 max-w-2xl text-lg leading-8 text-white/70"
              >
                Build inside a living ecosystem where intelligence is designed, orchestrated, visualized, and deployed into the real world.
                We call it the Sanctuary.
              </motion.p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full px-6">
                  <a href="#ecosystem">Enter the Ecosystem</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-6">
                  <a href="#partner-form">Become a Partner</a>
                </Button>
                <Button type="button" variant="ghost" className="rounded-full px-5 text-white/80 hover:bg-white/10 hover:text-white" onClick={downloadDeck}>
                  <Download className="mr-2 h-4 w-4" />
                  Download deck
                </Button>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-3">
                {[
                  ['Sanctuary', 'Living ecosystem'],
                  ['AI Theater', 'Systems in motion'],
                  ['Orchestrator', 'Coordinated execution'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4 backdrop-blur-sm">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/40">{label}</div>
                    <div className="mt-2 text-sm font-medium text-white/85">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(7,12,20,0.98))] p-4 sm:p-6">
              <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-black/20">
                <img
                  src="/assets/partnership/scientist-in-lab-stockcake.webp"
                  alt="Scientist working in a lab with advanced instrumentation"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                  <div className="mt-4 max-w-lg text-lg font-medium text-white sm:text-xl">
                    From blueprint to research, MapleOS gives humans the tools to do remarkable things.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(5,8,14,0.98),rgba(8,12,20,0.94))]">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
            <div className="rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top,rgba(138,92,246,0.14),transparent_36%),linear-gradient(180deg,rgba(12,18,28,0.96),rgba(7,12,20,0.96))] p-4 shadow-[0_28px_120px_rgba(0,0,0,0.38)] sm:p-6">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="max-w-3xl">
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                    Oncology research showcase.
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-white/65 sm:text-base">
                    In this researcher-led project we conducted a multi-agent research synthesis on pancreatic cancer using our Blueprint Orchestrator. Over 3900 academic and government sources were analyzed to identify 5-10 promising treatment directions, key uncertainties, and suggested next-steps for future research.
                  </p>
                </div>
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs uppercase tracking-[0.24em] text-white/55">
                  Research showcase
                </div>
              </div>

              <div className="overflow-hidden rounded-[28px] border border-white/10 bg-black/40">
                <video
                  src="/assets/partnership/ai-orchestrator-oncology.webm"
                  className="aspect-video w-full bg-black object-cover"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="mt-3 text-sm leading-7 text-white/65 sm:text-base">
                The Orchestrator is a key component of the MapleOS platform that allows for the coordination and management of multiple AI agents working together to achieve a common goal. It provides a way to define the roles and responsibilities of each agent, as well as the rules and constraints that govern their interactions.
              </p>
            </div>
          </div>
        </section>

        <section id="ecosystem" className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-sm font-medium uppercase tracking-[0.28em] text-primary/80">The Sanctuary</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">A shared ecosystem for living intelligence</h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">
                The Sanctuary is where builders, enterprises, and systems connect — creating intelligence that can evolve into trusted human products.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {sanctuaryPillars.map((pillar) => (
                <Card key={pillar.title} className="border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.94),rgba(8,12,20,0.94))]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl">
                      <pillar.icon className="h-5 w-5 text-primary" />
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="leading-7 text-muted-foreground">{pillar.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-white/[0.02] py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.88fr_1.12fr] lg:items-start lg:px-8">
            <div>
              <div className="text-sm font-medium uppercase tracking-[0.28em] text-primary/80">AI Theater</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Design intelligence as a system. Watch it execute in real time.</h2>
              <p className="mt-4 max-w-xl text-lg leading-8 text-muted-foreground">
                This is where Blueprint Canvas, AI Theater, and the Orchestrator come together — structure, execution, and coordination in one living workflow.
              </p>
            </div>

            <div className="grid gap-4">
              {ecosystemMoments.map((item) => (
                <Card key={item.title} className="border-white/10 bg-background/70">
                  <CardContent className="p-6">
                    <div className="text-xs uppercase tracking-[0.24em] text-primary/80">{item.eyebrow}</div>
                    <h3 className="mt-3 text-xl font-semibold">{item.title}</h3>
                    <p className="mt-3 leading-7 text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
              <div>
                <div className="text-sm font-medium uppercase tracking-[0.28em] text-primary/80">Ecosystem roles</div>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Built for the people who turn systems into markets</h2>
                <p className="mt-4 max-w-xl text-lg leading-8 text-muted-foreground">
                  Whether you are creating systems, operating them, or bringing them to customers, MapleOS gives you a calm, visual, product-first environment to grow inside.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild className="rounded-full px-6">
                    <Link to="/start">
                      Start building
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button type="button" variant="outline" className="rounded-full px-6" onClick={downloadDeck}>
                    <Download className="mr-2 h-4 w-4" />
                    Partnership deck
                  </Button>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {ecosystemRoles.map((role) => (
                  <Card key={role.title} className="border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.94),rgba(8,12,20,0.94))]">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-xl">
                        <role.icon className="h-5 w-5 text-primary" />
                        {role.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="leading-7 text-muted-foreground">{role.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-muted/20 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-sm font-medium uppercase tracking-[0.28em] text-primary/80">Why partner</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Not another program. A pathway into a new computing paradigm.</h2>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {partnerBenefits.map((benefit) => (
                <Card key={benefit.title} className="border-white/10 bg-background/70">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl">
                      <benefit.icon className="h-5 w-5 text-primary" />
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="leading-7 text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(7,12,20,0.98))] px-6 py-10 sm:px-10 lg:px-12 lg:py-12">
              <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
                <div>
                  <div className="text-sm font-medium uppercase tracking-[0.28em] text-primary/80">Become a partner</div>
                  <h2 id="partner-form" className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                    Enter the Sanctuary. Build what comes next.
                  </h2>
                  <p className="mt-4 max-w-xl text-lg leading-8 text-white/65">
                    Tell us what you are building, where you want to scale, and how you want to participate in the MapleOS ecosystem.
                  </p>
                </div>

                <div className="rounded-[28px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="companyName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company name</FormLabel>
                              <FormControl>
                                <Input placeholder="Your Company Inc." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="contactName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Your name</FormLabel>
                              <FormControl>
                                <Input placeholder="Jane Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="contactEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="jane.doe@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="website"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company website</FormLabel>
                            <FormControl>
                              <Input placeholder="https://yourcompany.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>What do you want to build in the Sanctuary?</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Tell us about your systems, your customers, and how you see MapleOS fitting into your ecosystem."
                                className="min-h-[140px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex flex-wrap gap-3">
                        <Button type="submit" disabled={isLoading} className="rounded-full px-6">
                          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Submit request
                        </Button>
                        <Button type="button" variant="outline" className="rounded-full px-6" onClick={downloadDeck}>
                          <Download className="mr-2 h-4 w-4" />
                          Download deck
                        </Button>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </LandingShell>
  );
}